import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import axios from "axios";
import Highcharts from "highcharts";
import WindBarbs from "highcharts/modules/windbarb";
import { useDispatch, useSelector } from "react-redux";
import Draggable from "react-draggable";
import { selectcqlData, setcqlData } from "../../../features/layers/cqlDataSlice";
import { selectLayerDataSet } from "../../../features/layers/overlaylayerslice";
import { FaTimes } from "react-icons/fa";
import { useMap } from "react-leaflet";

// Initialize windbarb module
if (typeof Highcharts === 'object') {
    WindBarbs(Highcharts);
}

const Meteogram = ({ position, location, onClose }) => {
  const dispatch = useDispatch();
  const map = useMap();
  const data = useSelector(selectLayerDataSet);
  
  // State
  const [dates, setDates] = useState();
  const [odates, setoDates] = useState();
  const [toggle, setToggle] = useState(false);
  const [locationName, setLocationName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  
  // Dimensions state
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Track window dimensions for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      // Force chart reflow on resize to fit new container dimensions
      if (chartRef.current) {
        chartRef.current.reflow();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Disable map interactions when modal is open
  useEffect(() => {
    if (map) {
      if (toggle) {
        map.dragging.disable();
        map.scrollWheelZoom.disable();
        map.doubleClickZoom.disable();
      } else {
        map.dragging.enable();
        map.scrollWheelZoom.enable();
        map.doubleClickZoom.enable();
      }
    }

    return () => {
      if (map) {
        map.dragging.enable();
        map.scrollWheelZoom.enable();
        map.doubleClickZoom.enable();
      }
    };
  }, [map, toggle]);

  // Parse layer data to get parameters
  useEffect(() => {
    const generateTimeSteps = (layerData) => {
      if (layerData.show && layerData.noofdays && layerData.class === 'Model') {
        setDates(layerData.date);
        setoDates(layerData.odate);
      }
    };
    if (Array.isArray(data)) {
      data.forEach((overlayer) => generateTimeSteps(overlayer));
    }
  }, [data]);

  // --- Meteogram Class Logic ---
  function MeteogramClass(json, containerId) {
    this.symbols = [];
    this.precipitations = [];
    this.winds = [];
    this.temperatures = [];
    this.relativeHumidity = [];
    this.rainfall = [];
    this.json = json;
    this.container = containerId;
    this.parseYrData();
  }

  // Generate Highcharts options
  MeteogramClass.prototype.getChartOptions = function (startTime) {
    const temperatureValues = this.temperatures.map(point => point.y);
    const minTemp = Math.min(...temperatureValues);
    const maxTemp = Math.max(...temperatureValues);
    const maxRainfall = Math.max(...this.rainfall.map(point => point.y));
    
    // Check if mobile for responsive config
    const isMobile = dimensions.width <= 768;

    return {
      chart: {
        renderTo: this.container,
        marginBottom: isMobile ? 70 : 100,
        marginRight: isMobile ? 20 : 60,
        marginTop: isMobile ? 60 : 60,
        marginLeft: isMobile ? 50 : 70,
        plotBorderWidth: 1,
        height: null, 
        alignTicks: false,
        scrollablePlotArea: { 
          // Enables horizontal scrolling on mobile
          minWidth: isMobile ? 700 : 900,
          scrollPositionX: 0,
          opacity: 1
        },
        events: {
          load: function() {
            // Ensure pointer events are enabled for mobile
            if (isMobile) {
              this.container.style.pointerEvents = 'auto';
              // Allow horizontal panning
              this.container.style.touchAction = 'pan-x'; 
            }
          }
        }
      },
      title: { 
        text: "Meteogram", 
        align: "center",
        style: {
          fontSize: isMobile ? '16px' : '20px',
          fontWeight: 'bold'
        },
        margin: 15
      },
      
      exporting: { enabled: false },
      credits: { enabled: false },
      tooltip: {
        shared: true,
        useHTML: true,
        padding: 10,
        borderWidth: 1,
        borderRadius: 6,
        shadow: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        // CRITICAL FIX: Stops tooltip from dragging, makes it snap on tap
        followTouchMove: false,
        style: {
          fontSize: isMobile ? '12px' : '13px',
          lineHeight: '1.5'
        },
        formatter: function() {
          const point = this.points[0];
          const utcDate = new Date(point.x);
          
          // Convert UTC to IST (UTC + 5:30)
          const istDate = new Date(utcDate.getTime() + 19800000);
          
          const pad = (n) => n.toString().padStart(2, '0');
          const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          
          // IST Components
          const istDay = dayNames[istDate.getUTCDay()];
          const istDateNum = istDate.getUTCDate();
          const istMonth = istDate.getUTCMonth() + 1;
          let istHour = istDate.getUTCHours();
          const istMin = istDate.getUTCMinutes();
          const ampm = istHour >= 12 ? 'PM' : 'AM';
          const istHour12 = istHour % 12 || 12;
          const istStr = `${istHour12}:${pad(istMin)} ${ampm}`;
          const istDateStr = `${istDay}, ${pad(istDateNum)}/${pad(istMonth)}`;

          // UTC Components
          const utcHour = pad(utcDate.getUTCHours());
          const utcMin = pad(utcDate.getUTCMinutes());
          const utcStr = `${utcHour}:${utcMin}Z`;

          let tooltipHTML = `
            <div style="border-bottom: 1px solid #ddd; padding-bottom: 6px; margin-bottom: 6px;">
              <div style="color: #d32f2f; font-weight: bold;">IST: ${istDateStr} ${istStr}</div>
              <div style="color: #1976d2; font-size: 0.9em;">UTC: ${utcStr}</div>
            </div>
          `;
          
          this.points.forEach(point => {
            let value;
            let unit = '';
            let direction = '';
            
            if (point.series.name === 'Wind') {
              value = point.point.value || point.y || 0;
              unit = ' m/s';
              if (point.point.direction !== undefined) {
                direction = ` (${point.point.direction}°)`;
              }
            } else {
              value = point.y || 0;
              switch(point.series.name) {
                case 'Temperature': unit = '°C'; break;
                case 'Rainfall': unit = ' mm'; break;
                case 'Relative Humidity': unit = '%'; break;
                default: break;
              }
            }
            
            const formattedValue = typeof value === 'number' ? value.toFixed(1) : '0.0';
            
            tooltipHTML += `
              <div style="display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 2px;">
                <span><span style="color:${point.color}">\u25CF</span> ${point.series.name}:</span> 
                <span style="font-weight: bold;">${formattedValue}${unit}${direction}</span>
              </div>
            `;
          });
          
          return tooltipHTML;
        },
        valueDecimals: 1
      },
      xAxis: [
        {
          type: "datetime",
          tickInterval: 2 * 36e5, // 2 hours
          gridLineWidth: 1,
          gridLineColor: "rgba(128, 128, 128, 0.1)",
          offset: isMobile ? 25 : 30,
          labels: { 
            formatter: function() {
                const date = new Date(this.value + 19800000); // Shift to IST
                return Highcharts.dateFormat('%H', date.getTime());
            },
            style: {
              fontSize: isMobile ? '10px' : '12px',
              color: '#444',
              fontWeight: 'bold'
            },
            y: isMobile ? 15 : null
          },
          crosshair: {
            width: 1,
            color: '#999',
            dashStyle: 'solid'
          },
        },
        {
          linkedTo: 0,
          type: "datetime",
          tickInterval: 24 * 3600 * 1000, // 1 day
          labels: {
            formatter: function() {
              const date = new Date(this.value + 19800000);
              const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
              const day = dayNames[date.getUTCDay()];
              const d = date.getUTCDate();
              return isMobile ? day : `${day} ${d}`;
            },
            align: "left",
            x: 5,
            y: -5,
            style: {
                fontSize: isMobile ? '10px' : '12px',
                color: '#666'
            }
          },
          opposite: true,
          tickLength: 20,
          gridLineWidth: 1,
        },
      ],
      yAxis: [
        {
          title: { 
            text: isMobile ? "Temp" : "Temperature (°C)",
            style: {
              fontSize: isMobile ? '10px' : '10px',
              color: '#FF3333',
              fontWeight: 'bold'
            },
            margin: isMobile ? 0 : 8
          },
          labels: {
            format: "{value}°",
            style: { 
              fontSize: isMobile ? "10px" : "10px",
              color: "#FF3333",
              fontWeight: '500'
            },
            x: -5,
          },
          plotLines: [{ value: 0, color: "#BBBBBB", width: 1, zIndex: 2 }],
          max: maxTemp + 5,
          min: minTemp - 5,
          tickInterval: 5,
          gridLineColor: "rgba(128, 128, 128, 0.1)",
        },
        {
          title: { 
            text: isMobile ? "Rain" : "Rainfall (mm/hr)",
            style: {
              fontSize: isMobile ? '10px' : '10px',
              color: '#68CFE8',
              fontWeight: 'bold'
            },
            margin: isMobile ? 0 : 8
          },
          labels: { 
            style: { 
              fontSize: isMobile ? "10px" : "10px",
              color: "#68CFE8",
              fontWeight: '500'
            }, 
            y: 2, 
            x: 5
          },
          gridLineWidth: 0,
          tickLength: 0,
          min: 0,
          max: maxRainfall * 3,
        },
        {
          title: { 
            text: isMobile ? "RH%" : "Relative Humidity (%)",
            style: {
              fontSize: isMobile ? '10px' : '10px',
              color: Highcharts.getOptions().colors[4],
              fontWeight: 'bold'
            },
            margin: isMobile ? 0 : 8
          },
          labels: {
            style: { 
              fontSize: isMobile ? "10px" : "10px",
              color: Highcharts.getOptions().colors[4],
              fontWeight: '500'
            },
            y: 2,
            x: 15,
          },
          gridLineWidth: 0,
          opposite: true,
          showLastLabel: false,
          min: 0,
          max: 100,
        },
      ],
      legend: {
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
        floating: false, 
        backgroundColor: "rgba(255,255,255,0.9)",
        borderWidth: 0,
        borderRadius: 0,
        shadow: false,
        padding: 10,
        itemStyle: {
          fontSize: isMobile ? '11px' : '13px',
          color: '#333',
          fontWeight: '500'
        },
        itemDistance: isMobile ? 10 : 20,
        margin: 0
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          borderWidth: 0,
          groupPadding: 0,
          pointPadding: 0.05,
        },
        series: { 
          pointPlacement: "between",
          states: { hover: { lineWidthPlus: 1 } }
        },
        // Removed manual touch events here to let Highcharts handle them via 'followTouchMove: false'
      },
      series: [
        {
          name: "Temperature",
          data: this.temperatures,
          type: "spline",
          marker: { enabled: false },
          zIndex: 1,
          color: "#FF3333",
          negativeColor: "#48AFE8",
          lineWidth: isMobile ? 2 : 3
        },
        {
          name: "Rainfall",
          type: "column",
          color: "#68CFE8",
          data: this.rainfall,
          yAxis: 1,
          borderWidth: 0,
          groupPadding: 0,
          pointPadding: 0.1,
          pointWidth: isMobile ? 8 : 12,
        },
        {
          name: "Relative Humidity",
          color: Highcharts.getOptions().colors[4],
          data: this.relativeHumidity,
          type: "spline",
          marker: { enabled: false },
          shadow: false,
          yAxis: 2,
          lineWidth: isMobile ? 2 : 3
        },
        {
          name: "Wind",
          type: "windbarb",
          id: "windbarbs",
          color: Highcharts.getOptions().colors[1],
          lineWidth: 1.5,
          data: this.winds,
          vectorLength: isMobile ? 14 : 18,
          yOffset: isMobile ? -8 : -12,
          tooltip: {
            valueSuffix: ' m/s',
            pointFormatter: function() {
              let directionText = '';
              if (this.direction !== undefined) {
                directionText = ` (${this.direction}°)`;
              }
              return `${this.series.name}: <b>${this.value} m/s${directionText}</b>`;
            }
          }
        },
      ],
    };
  };

  MeteogramClass.prototype.parseYrData = function () {
    if (!this.json || !this.json.output || this.json.output.length === 0) {
      setError("No data received from server");
      setLoading(false);
      return;
    }
    
    const firstTimestamp = this.json.output[0].time;
    const startTime = this.parseTimeToUTC(firstTimestamp);
    
    const dataByTime = {};
    this.json.output.forEach((node) => {
      if (!dataByTime[node.time]) {
        dataByTime[node.time] = node;
      }
    });

    Object.values(dataByTime).forEach((node) => {
      const x = this.parseTimeToUTC(node.time);
      this.temperatures.push({ x, y: node.air_temperature });
      this.relativeHumidity.push({ x, y: node.relative_humidity });
      this.rainfall.push({ x, y: node.rainfall });
    });
    
    Object.values(dataByTime).forEach((node) => {
      const x = this.parseTimeToUTC(node.time);
      const date = new Date(x);
      const hour = date.getUTCHours();
      
      if (hour % 4 === 0) {
        this.winds.push({ 
          x, 
          value: node.wind_speed, 
          direction: node.wind_from_direction 
        });
      }
    });
    
    const sortByX = (a, b) => a.x - b.x;
    this.temperatures.sort(sortByX);
    this.relativeHumidity.sort(sortByX);
    this.rainfall.sort(sortByX);
    this.winds.sort(sortByX);
    
    this.createChart(startTime);
  };

  MeteogramClass.prototype.parseTimeToUTC = function (timeString) {
    const match = timeString.match(/^(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})$/);
    if (!match) {
      console.error('Invalid time format:', timeString);
      return Date.now();
    }
    
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const hour = parseInt(match[4], 10);
    const minute = parseInt(match[5], 10);
    const second = parseInt(match[6], 10);
    
    return Date.UTC(year, month, day, hour, minute, second);
  };

  MeteogramClass.prototype.createChart = function (startTime) {
    const chartOptions = this.getChartOptions(startTime);
    
    this.chart = new Highcharts.Chart(chartOptions, () => {
      setTimeout(() => {
        setLoading(false);
      }, 500);
      
      chartRef.current = this.chart;
    });
  };

  // --- End Meteogram Class ---

  // Fetch weather data
  const getWeather = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const coords = position?.location || location;
      if (!coords?.lat || !coords?.lng) {
        throw new Error("Invalid coordinates");
      }

      const response = await axios.get(
        `https://api.ncmrwf.gov.in/meteogram?coords=${coords.lat},${coords.lng}&date=${dates}&odate=${odates}`,
        {
          timeout: 15000
        }
      );
      
      if (!response.data || !response.data.output) {
        throw new Error("Invalid data format from API");
      }
      
      setToggle(true);
      window.meteogram = new MeteogramClass(response.data, "meteogram-container");
    } catch (err) {
      setLoading(false);
      setError(err.message || "Failed to load meteogram data. Please try again later.");
      console.error("Meteogram error:", err);
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    dispatch(setcqlData({ show: false }));
    if (onClose) onClose();
  };

  const getLocationName = async (lat, lng) => {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
        { timeout: 5000 }
      );
      if (response.data.display_name) {
        const name = response.data.display_name;
        setLocationName(name);
        
        if (chartRef.current) {
          chartRef.current.update({
            subtitle: {
              text: name
            }
          }, false);
          chartRef.current.redraw();
        }
      }
    } catch (err) {
      console.error("Location name error:", err);
      setLocationName("Location not available");
    }
  };

  const handleContainerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Global touch handling: allow interaction with chart, prevent map drag
  useEffect(() => {
    if (!toggle) return;

    const handleGlobalTouchStart = (e) => {
      // If we are touching the chart container or its children, 
      // DO NOT stop propagation. Let Highcharts handle it.
      if (e.target.closest('#meteogram-container') || e.target.closest('.highcharts-container')) {
        return;
      }
      
      // Stop propagation to prevent map interactions underneath if not on the chart
      if (dimensions.width > 768) {
         e.stopPropagation();
      }
    };

    document.addEventListener('touchstart', handleGlobalTouchStart, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleGlobalTouchStart);
    };
  }, [toggle, dimensions.width]);

  // Main trigger effect
  useEffect(() => {
    const coords = position?.location || location;
    if (coords?.lat && coords?.lng && dates) {
      getLocationName(coords.lat, coords.lng);
      getWeather();
    }
  }, [position, location, dates, odates]);

  return (
    <>
      {loading && !error && (
        <LoaderModal>
          <div className="modal-content">
            <i className="fa fa-cog fa-spin" style={{ marginRight: "10px" }}></i>
            Computing Meteogram...
          </div>
        </LoaderModal>
      )}

      {error && (
        <ErrorModal>
          <div className="modal-content">
            <div className="error-text">
                <i className="fa fa-exclamation-triangle" style={{ color: "#ff3333" }}></i>
                <span>{error}</span>
            </div>
            <div className="button-group">
              <button onClick={() => {
                setError(null);
                getWeather();
              }} className="retry-button">
                Retry
              </button>
              <button onClick={handleClose} className="close-button">
                Close
              </button>
            </div>
          </div>
        </ErrorModal>
      )}

      {toggle && (
        <Draggable 
          handle=".headers" 
          bounds="parent"
          // Disable dragging on mobile to prevent UX issues
          disabled={dimensions.width <= 768}
          position={dimensions.width <= 768 ? {x: 0, y: 0} : undefined}
        >
          <ProfileContainer
            ref={containerRef}
            onClick={handleContainerClick}
            dimensions={dimensions}
            onTouchStart={(e) => {
              if (e.target.closest('.headers') || e.target.closest('.close')) {
                e.stopPropagation();
              }
            }}
            onTouchMove={(e) => {
              if (e.target.closest('.headers') || e.target.closest('.close')) {
                e.stopPropagation();
              }
            }}
          >
            <div className="headers">
              <div className="header-content">
                <div className="header-text">
                  <p className="title">Meteogram</p>
                  <p className="location">{locationName}</p>
                </div>
                <button 
                  className="close" 
                  onClick={handleClose} 
                  title="Close"
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    handleClose(e);
                  }}
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            {/* ID must match what is passed to Highcharts logic */}
            <div 
              id="meteogram-container" 
              className="chart-container"
            ></div>
          </ProfileContainer>
        </Draggable>
      )}
    </>
  );
};

export default Meteogram;

// --- STYLED COMPONENTS ---

const ProfileContainer = styled.div`
  background: #fff;
  z-index: 99999;
  border-radius: 10px;
  box-shadow: 0 0 25px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  
  /* CRITICAL: pan-x allows horizontal scrolling of chart but passes clicks/taps through */
  touch-action: ${({ dimensions }) => dimensions.width <= 768 ? 'pan-x' : 'none'};
  
  /* Responsive Positioning Logic */
  ${({ dimensions }) => {
    if (dimensions.width > 1200) {
      return `
        position: absolute;
        width: 1150px;
        height: 620px;
        left: calc(50% - 575px);
        top: 40px;
      `;
    } else if (dimensions.width > 992) {
      return `
        position: absolute;
        width: 1050px;
        height: 580px;
        left: calc(50% - 525px);
        top: 35px;
      `;
    } else if (dimensions.width > 768) {
      return `
        position: absolute;
        width: 950px;
        height: 530px;
        left: calc(50% - 475px);
        top: 30px;
      `;
    } else {
      /* Mobile positioning */
      return `
        position: fixed !important;
        width: 95vw;
        height: 80vh;
        left: 50% !important;
        top: 50% !important;
        transform: translate(-50%, -50%) !important;
        border: 1px solid #ddd;
        max-width: 95vw;
        max-height: 85vh;
      `;
    }
  }}

  .headers {
    padding: ${({ dimensions }) => dimensions.width > 768 ? '15px 20px' : '10px 15px'};
    cursor: ${({ dimensions }) => dimensions.width > 768 ? 'move' : 'default'};
    border-bottom: 2px solid #e0e0e0;
    user-select: none;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    flex-shrink: 0; 
    
    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;
    }
    
    .header-text {
      flex: 1;
      min-width: 0; 
      overflow: hidden;
    }
    
    .title {
      font-weight: bold;
      color: #222;
      margin: 0;
      font-size: ${({ dimensions }) => dimensions.width > 768 ? '20px' : '16px'};
    }
    
    .location {
      color: #444;
      margin: 0;
      font-size: ${({ dimensions }) => dimensions.width > 768 ? '14px' : '12px'};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  
  .close {
    cursor: pointer;
    color: #666;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    touch-action: manipulation;
    
    &:hover {
      color: #ff3333;
      border-color: #ff3333;
    }
  }
  
  .chart-container {
    flex: 1; 
    width: 100%;
    padding: 0;
    background: #fff;
    overflow: hidden;
    position: relative;
    pointer-events: auto !important;
    touch-action: pan-x !important;
    
    /* Force Highcharts to be interactive */
    .highcharts-container {
      width: 100% !important;
      height: 100% !important;
      pointer-events: auto !important;
      touch-action: pan-x !important;
    }
    
    .highcharts-root {
      pointer-events: auto !important;
      touch-action: pan-x !important;
    }
    
    .highcharts-series,
    .highcharts-point,
    .highcharts-tracker,
    .highcharts-tooltip-container {
      pointer-events: auto !important;
      touch-action: pan-x !important;
    }
  }
`;

const LoaderModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100000;
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(2px);

  .modal-content {
    background: white;
    padding: clamp(15px, 5vw, 25px) clamp(20px, 6vw, 30px);
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    font-size: clamp(15px, 4vw, 18px);
    font-weight: 500;
    color: #3f4854;
    display: flex;
    align-items: center;
    max-width: 90%;
    width: auto;
    min-width: 200px;
    text-align: center;
    border: 1px solid rgba(0, 0, 0, 0.1);

    @media (max-width: 480px) {
      flex-direction: column;
      gap: 12px;
    }

    .fa-cog {
      font-size: clamp(18px, 5vw, 22px);
      margin-right: clamp(8px, 2vw, 12px);

      @media (max-width: 480px) {
        margin-right: 0;
        margin-bottom: 8px;
      }
    }
  }
`;

const ErrorModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100000;
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(2px);

  .modal-content {
    background: white;
    padding: clamp(15px, 5vw, 25px) clamp(15px, 5vw, 30px);
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    font-size: clamp(15px, 4vw, 18px);
    font-weight: 500;
    color: #3f4854;
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 90%;
    width: auto;
    min-width: 200px;
    text-align: center;
    border: 1px solid rgba(0, 0, 0, 0.1);

    .fa-exclamation-triangle {
      font-size: clamp(20px, 6vw, 24px);
      margin-bottom: clamp(8px, 2vw, 12px);
    }
  }

  .button-group {
    display: flex;
    gap: clamp(8px, 3vw, 15px);
    margin-top: clamp(12px, 3vw, 20px);
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
  }

  .retry-button, .close-button {
    padding: clamp(6px, 2vw, 8px) clamp(12px, 4vw, 20px);
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: clamp(13px, 3vw, 14px);
    transition: all 0.2s ease;
    min-width: 80px;
    flex: 1;
    max-width: 120px;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    &:active {
      transform: translateY(0);
      box-shadow: none;
    }
  }

  .retry-button {
    background: #3f4854;
    color: white;

    &:hover {
      background: #2c343d;
    }
  }

  .close-button {
    background: #f0f0f0;
    color: #333;

    &:hover {
      background: #e0e0e0;
    }
  }
`;